import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import {
  Share as ShareIcon,
  Schedule,
  Store,
  CheckCircle,
  Info,
  Visibility,
  TrendingUp,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import ScrollIndicator, {
  BottomFadeOverlay,
} from "../../components/common/ScrollIndicator";
import {
  useGetOfferByIdQuery,
  useIncrementOfferShareMutation,
  useIncrementOfferViewMutation,
} from "../../features/offers/offersApiSlice";
import { getCategoryConfig } from "../../utils/offerHelpers";
import {
  DetailHero,
  DetailSkeleton,
  DetailErrorState,
  DetailShareBar,
  DetailFloatingButton,
  useDetailShare,
  useScrollEffect,
} from "../../components/Details";

const OfferDetails: React.FC = () => {
  const navigate = useNavigate();
  const { offerId } = useParams<{ mallId: string; offerId: string }>();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareBar, setShowShareBar] = useState(true);
  const [promoSnackbar, setPromoSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const contentRef = useRef<HTMLDivElement>(null);

  // Fetch offer details (backend auto-increments view count)
  const {
    data: offer,
    isLoading,
    error,
  } = useGetOfferByIdQuery(offerId || "", {
    skip: !offerId,
  });

  // Track share mutation
  const [incrementShare] = useIncrementOfferShareMutation();

  // Track view mutation
  const [incrementView] = useIncrementOfferViewMutation();

  // Format date helper
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Share functionality with localStorage tracking
  const shareHandlers = useDetailShare({
    itemId: offerId || "",
    itemType: "offer",
    shareText: offer
      ? `🎉 Check out this amazing offer!\n\n${offer.title}\n${
          offer.discountText
        }\n\nValid until ${formatDate(offer.validUntil || offer.expiresAt)}\n\n`
      : "",
    incrementShareMutation: incrementShare,
  });
  // Increment view count once per user (localStorage tracking)
  useEffect(() => {
    if (offer && offerId) {
      const viewKey = `offer_viewed_${offerId}`;
      const hasViewed = localStorage.getItem(viewKey);

      if (!hasViewed) {
        // First time viewing - increment count and mark as viewed
        incrementView({ offerId, mallId: offer.mallId });
        localStorage.setItem(viewKey, "true");
      }
    }
  }, [offer, offerId, incrementView]);

  // Smooth scroll effect using custom hook
  useScrollEffect(contentRef, setShowShareBar, 300);

  // Handle image carousel navigation
  const images = offer?.imageUrl
    ? [offer.imageUrl, offer.imageUrl, offer.imageUrl]
    : ["/placeholder-offer.jpg"];

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (error || !offer) {
    return (
      <DetailErrorState onBack={() => navigate(-1)} message="Offer not found" />
    );
  }

  const categoryConfig = getCategoryConfig(offer.category);

  // Build meta info for hero - simplified like EventDetails/StoreDetails
  const heroMetaInfo = (
    <>
      {/* Store Info with Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, md: 2 },
          mb: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            borderRadius: 1.5,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {offer.storeLogoUrl ? (
            <img
              src={offer.storeLogoUrl}
              alt={offer.storeName}
              style={{
                width: 32,
                height: 32,
                objectFit: "contain",
                borderRadius: 6,
              }}
            />
          ) : (
            <Store sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }} />
          )}
        </Box>
        <Box>
          <Typography
            fontWeight={700}
            sx={{
              color: "white",
              textShadow: "0 2px 8px rgba(0,0,0,0.3)",
              fontSize: { xs: "0.938rem", md: "1rem" },
            }}
          >
            {offer.storeName}
          </Typography>
          {offer.storeLocation && (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.95)",
                textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                fontSize: { xs: "0.813rem", md: "0.875rem" },
              }}
            >
              {offer.storeLocation}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Validity Period */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, md: 2 },
          mb: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            borderRadius: 1.5,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Schedule sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }} />
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: { xs: "0.875rem", md: "0.938rem" },
            textShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
        >
          Valid: {formatDate(offer.validFrom)} -{" "}
          {formatDate(offer.validUntil || offer.expiresAt || offer.validFrom)}
        </Typography>
      </Box>

      {/* View and Share Counts */}
      {(offer.viewCount !== undefined || offer.shareCount !== undefined) && (
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2.5, md: 3 },
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {offer.viewCount !== undefined && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 1.5,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Visibility
                  sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", md: "0.938rem" },
                  textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                {offer.viewCount.toLocaleString()} views
              </Typography>
            </Box>
          )}

          {offer.shareCount !== undefined && offer.shareCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 1.5,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShareIcon
                  sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", md: "0.938rem" },
                  textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                {offer.shareCount.toLocaleString()} shares
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        pb: 3,
      }}
    >
      {/* Hero Section with DetailHero */}
      <DetailHero
        images={images}
        currentImageIndex={currentImageIndex}
        title={offer.title}
        categoryLabel={offer.category.toUpperCase()}
        gradientColors={categoryConfig.gradient}
        // statusBadge={discountBadge}
        metaInfo={heroMetaInfo}
        onBack={() => navigate(-1)}
        onPrevImage={images.length > 1 ? handlePrevImage : undefined}
        onNextImage={images.length > 1 ? handleNextImage : undefined}
      />

      {/* Main Content - Slides up over hero */}
      <Box
        ref={contentRef}
        sx={{
          position: "relative",
          zIndex: 10,
          background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
          borderTopLeftRadius: { xs: 20, md: 32 },
          borderTopRightRadius: { xs: 20, md: 32 },
          boxShadow: "0 -8px 32px rgba(0,0,0,0.12)",
          willChange: "transform",
          mt: { xs: -2, md: -3 },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 3, md: 4 }, pb: 2, px: { xs: 2, sm: 3 } }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {/* Title Section */}
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                mb: 2,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                lineHeight: 1.2,
                background: categoryConfig.gradient,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {offer.title}
            </Typography>

            {/* Description Card */}
            <Card
              elevation={0}
              sx={{
                bgcolor: "white",
                borderRadius: 3,
                p: { xs: 2.5, md: 3 },
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                color="text.secondary"
                sx={{
                  lineHeight: 1.8,
                  fontSize: { xs: "0.938rem", md: "1rem" },
                }}
              >
                {offer.fullDescription || offer.description}
              </Typography>
            </Card>

            {/* Promo Code */}
            {offer.promoCode && (
              <Card
                elevation={0}
                sx={{
                  background: categoryConfig.gradient,
                  borderRadius: 3,
                  p: 3,
                  mb: 3,
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `
                      repeating-linear-gradient(
                        45deg,
                        rgba(255,255,255,0.1),
                        rgba(255,255,255,0.1) 10px,
                        transparent 10px,
                        transparent 20px
                      )
                    `,
                  },
                }}
              >
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 1,
                      fontWeight: 700,
                      color: "rgba(255,255,255,0.9)",
                      letterSpacing: 1,
                    }}
                  >
                    PROMO CODE
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{
                        color: "white",
                        letterSpacing: 3,
                        fontFamily: "monospace",
                        textShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      }}
                    >
                      {offer.promoCode}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        const promoCode = offer.promoCode || "";
                        try {
                          // Modern Clipboard API (works on HTTPS and modern browsers)
                          if (
                            navigator.clipboard &&
                            navigator.clipboard.writeText
                          ) {
                            await navigator.clipboard.writeText(promoCode);
                          } else {
                            // Fallback for older browsers or mobile devices
                            const textArea = document.createElement("textarea");
                            textArea.value = promoCode;
                            textArea.style.position = "fixed";
                            textArea.style.left = "-999999px";
                            textArea.style.top = "-999999px";
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();

                            const successful = document.execCommand("copy");
                            document.body.removeChild(textArea);

                            if (!successful) {
                              throw new Error("Copy command failed");
                            }
                          }

                          setPromoSnackbar({
                            open: true,
                            message: "Promo code copied to clipboard!",
                            severity: "success",
                          });
                        } catch (err) {
                          setPromoSnackbar({
                            open: true,
                            message: "Failed to copy promo code",
                            severity: "error",
                          });
                        }
                      }}
                      sx={{
                        bgcolor: "white",
                        color: "primary.main",
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.95)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      Copy Code
                    </Button>
                  </Box>
                </Box>
              </Card>
            )}

            {/* Details Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 1.5, md: 2 },
                mb: { xs: 2.5, md: 3 },
              }}
            >
              {/* Combined Validity Section */}
              <Card
                elevation={0}
                sx={{
                  background:
                    "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                  borderRadius: { xs: 2, md: 3 },
                  p: { xs: 2, md: 2.5 },
                  border: "2px solid",
                  borderColor: "success.light",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, md: 2 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 48, md: 56 },
                      height: { xs: 48, md: 56 },
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
                    }}
                  >
                    <CheckCircle
                      sx={{ color: "white", fontSize: { xs: 28, md: 32 } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        display: "block",
                        color: "success.dark",
                        fontWeight: 700,
                        mb: 0.5,
                        letterSpacing: 0.5,
                        fontSize: { xs: "0.688rem", md: "0.75rem" },
                      }}
                    >
                      VALIDITY PERIOD
                    </Typography>
                    <Typography
                      fontWeight={700}
                      sx={{
                        color: "success.dark",
                        fontSize: { xs: "0.813rem", md: "0.938rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {formatDate(offer.validFrom)}
                      <Typography
                        component="span"
                        sx={{
                          mx: 0.5,
                          fontWeight: 500,
                          fontSize: { xs: "0.75rem", md: "0.813rem" },
                        }}
                      >
                        to
                      </Typography>
                      {formatDate(offer.validUntil || offer.expiresAt)}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>

            {/* Engagement Stats */}
            <Card
              elevation={0}
              sx={{
                background: categoryConfig.gradient,
                borderRadius: 3,
                p: 3,
                mb: 3,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                  gap: 3,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Visibility sx={{ fontSize: 32, color: "white", mb: 1 }} />
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: "white", mb: 0.5 }}
                  >
                    {offer.viewCount?.toLocaleString() || "0"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}
                  >
                    Views
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <ShareIcon sx={{ fontSize: 32, color: "white", mb: 1 }} />
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: "white", mb: 0.5 }}
                  >
                    {offer.shareCount?.toLocaleString() || "0"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}
                  >
                    Shares
                  </Typography>
                </Box>
                <Box
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  <TrendingUp sx={{ fontSize: 32, color: "white", mb: 1 }} />
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: "white", mb: 0.5 }}
                  >
                    Trending
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}
                  >
                    Popular
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Terms & Conditions */}
            {offer.terms && (
              <Card
                elevation={0}
                sx={{
                  bgcolor: "#e3f2fd",
                  borderRadius: 3,
                  p: 3,
                  border: "2px solid",
                  borderColor: "info.light",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "info.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Info sx={{ color: "white", fontSize: 24 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} color="info.dark">
                    Terms & Conditions
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.8,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {offer.terms}
                </Typography>
              </Card>
            )}

            {/* Tags */}
            {offer.tags && offer.tags.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {offer.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={`#${tag}`}
                    sx={{
                      background:
                        "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      px: 1,
                      height: 36,
                    }}
                  />
                ))}
              </Box>
            )}
          </motion.div>
        </Container>
      </Box>

      {/* Bottom Sticky Share Bar */}
      <DetailShareBar
        show={showShareBar}
        onWhatsAppShare={shareHandlers.handleWhatsAppShare}
        onCopyLink={shareHandlers.handleCopyLink}
        onClose={() => setShowShareBar(false)}
      />

      {/* Floating WhatsApp Button */}
      <DetailFloatingButton
        show={!showShareBar}
        onClick={shareHandlers.handleWhatsAppShare}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={shareHandlers.snackbar.open}
        autoHideDuration={3000}
        onClose={shareHandlers.handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={shareHandlers.handleSnackbarClose}
          severity={shareHandlers.snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            fontWeight: 600,
          }}
        >
          {shareHandlers.snackbar.message}
        </Alert>
      </Snackbar>

      {/* Snackbar for promo code copy */}
      <Snackbar
        open={promoSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setPromoSnackbar({ ...promoSnackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setPromoSnackbar({ ...promoSnackbar, open: false })}
          severity={promoSnackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            fontWeight: 600,
          }}
        >
          {promoSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Scroll Indicator for Mobile Users */}
      <ScrollIndicator color="primary.main" bottomOffset={100} />
      <BottomFadeOverlay color="#f5f7fa" height={100} />
    </Box>
  );
};

export default OfferDetails;
